<template>
    <div class="member-edit guided-steps-page">
        <el-form class="form mt2" ref="memberChangePassword" :model="password" :rules="rules">
            <!-- Guided steps (backlog #17, same behaviour as 本人確認 #13): ① the passwords, ending
                 with the send button, which first checks ①'s fields; ② the code + submit, dimmed
                 AND locked until a code is sent in this page visit. The server flow is unchanged. -->
            <div class="iv-steps">
                <section :class="['iv-step', codeSent ? 'is-done' : 'is-active']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">
                            <i v-if="codeSent" class="el-icon-check" aria-hidden="true"></i>
                            <template v-else>1</template>
                        </span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">新しいパスワードを入力</h2>
                        <table class="table-register">
                            <tr>
                              <th id="old-password" class="column-label required">現在のパスワード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="oldPassword" class=""><el-input type="password" class="input" v-model="password.oldPassword" :disabled="disabled"></el-input></el-form-item>
                              </td>
                            </tr>
                            <tr class="password-row">
                              <th id="new-password" class="column-label required">新しいパスワード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="newPassword" class="">
                                      <el-input type="password" class="input" v-model="password.newPassword" :disabled="disabled"></el-input>
                                      <span class="d-inline-block small-text">小文字と数字を含めて8～32文字で組み合わせて設定してください</span>
                                  </el-form-item>
                              </td>
                            </tr>
                            <tr>
                              <th id="password-confirm" class="column-label required">新しいパスワード（確認）</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="newPasswordConfirm"><el-input type="password" class="input" v-model="password.newPasswordConfirm" :disabled="disabled"></el-input></el-form-item>
                              </td>
                            </tr>
                        </table>
                        <div class="iv-step__action">
                            <el-button type="primary" class="send-code-button" @click="handleSendVerificationCode" :disabled="disabled || verificationCodeCooldown > 0">本人確認コードを送信</el-button>
                            <span v-if="verificationCodeCooldown > 0" class="d-inline-block small-text">再送信可能まで30秒</span>
                        </div>
                    </div>
                </section>
                <section :class="['iv-step', codeSent ? 'is-active' : 'is-pending']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">2</span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">本人確認コードを入力</h2>
                        <table class="table-register">
                            <tr>
                              <th id="verification-code" class="column-label required">本人確認コード</th>
                              <td class="column-input max-width-400">
                                  <el-form-item prop="verificationCode">
                                      <el-input class="input verification-code-input" v-model="password.verificationCode" :disabled="stepTwoDisabled" placeholder="本人確認コード"></el-input>
                                      <span v-if="!codeSent" class="d-inline-block small-text">先に本人確認コードを送信してください</span>
                                  </el-form-item>
                              </td>
                            </tr>
                        </table>
                        <div class="iv-step__action text-center">
                            <div v-if="isComplete">
                                <div class="complete-wording">
                                    変更が完了しました。
                                </div>
                                <br>
                                <el-button type="primary" @click="$router.go(-1)">戻る</el-button>
                            </div>
                            <div v-else>
                                <div class="confirm-wording">
                                    上記内容で登録します。よろしいですか？
                                </div>
                                <br>
                                <el-button class="gtm_member_pwhenkou_member" @click="handleEditInformMember" :disabled="stepTwoDisabled"><span class="gtm_member_pwhenkou_member">{{ $t('commons.edit') }}</span></el-button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </el-form>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import ApiService from '@/common/api.service'
    import JwtService from '@/common/jwt.service'
    import MemberClientApi from '@/api/member/member-client.js'
    import verificationCodeCooldownMixin from '@/mixins/verificationCodeCooldownMixin'
    export default {
      name: 'memberChangePassword',
      mixins: [verificationCodeCooldownMixin],
      data () {
            const newPassword = (rule, value, callback) => {
                if (this.password.oldPassword === this.password.newPassword) {
                    callback(new Error('新しいパスワードに現在のスワードは設定できません'))
                } else {
                    callback()
                }
            }
            const newPasswordConfirm = (rule, value, callback) => {
                if (value !== this.password.newPassword) {
                    callback(new Error('新しいパスワード（確認）と新しいパスワードが一致しません'))
                } else {
                    callback()
                }
            }
            return {
                password: {
                    oldPassword: '',
                    newPassword: '',
                    newPasswordConfirm: '',
                    verificationCode: ''
                },
                rules: {
                    oldPassword: [
                      { required: true,
                        pattern: /^(?=.*[a-z])(?=.*\d)[^¥]{8,32}$/,
                        message: '小文字と数字を含めて8文字以上で入力してください',
                        trigger: 'blur'
                      }
                    ],
                    newPassword: [
                      { required: true,
                        // PasswordPolicy.kt: 8+ chars, lowercase+digit required, "¥" forbidden.
                        // oldPassword's own rule is left as-is (re-entry of an existing password).
                        pattern: /^(?=.*[a-z])(?=.*\d)[^¥]{8,32}$/,
                        message: '小文字と数字を含めて8文字以上で入力してください',
                        trigger: 'blur'
                      },
                      { validator: newPassword, trigger: 'blur' }
                    ],
                    newPasswordConfirm: [
                      { validator: newPasswordConfirm, trigger: 'blur' }
                    ],
                    verificationCode: [
                      { required: true, message: '本人確認コードを入力してください', trigger: 'blur' }
                    ]
                },
                disabled: false,
                isConfirm: false,
                isError: false,
                isComplete: false,
                // Set once a code is sent successfully in this page visit; unlocks step ②.
                codeSent: false,
                prefectureMaster: []
            }
        },
        computed: {
            ...mapGetters(['currentUser']),
            stepTwoDisabled () {
                return this.disabled || !this.codeSent
            }
        },
        created () {
        },
        // beforeUpdate () {
        //     this.$refs['memberChangePassword'].validate((valid) => {
        //         if (valid) {
        //             this.isError = false
        //         } else {
        //             this.isError = true
        //         }
        //     })
        // },
        methods: {
            handleSendVerificationCode () {
                if (this.verificationCodeCooldown > 0) {
                    this.$message({
                        message: '本人確認コードは30秒に1回のみ送信できます',
                        type: 'error',
                        duration: 3000
                    })
                    return
                }
                // Step ① must be complete before a code is sent (same as 本人確認, #13).
                return this.validateStepOne().then(valid => {
                    if (!valid) {
                        return
                    }
                    return this.sendVerificationCode()
                })
            },
            validateStepOne () {
                const form = this.$refs['memberChangePassword']
                return Promise.all(['oldPassword', 'newPassword', 'newPasswordConfirm'].map(prop => new Promise(resolve => {
                    form.validateField(prop, errorMessage => resolve(!errorMessage))
                }))).then(results => results.every(Boolean))
            },
            sendVerificationCode () {
                // PASSWORD_CHANGE purposeの送信先は常にJWT subject (login_id) にサーバー側で
                // 解決される - targetIdentifierは不要
                return MemberClientApi.sendOtc({ purpose: 'PASSWORD_CHANGE' }).then(() => {
                    this.codeSent = true
                    this.$message({
                        message: 'お客様に本人確認コードを送信しました',
                        type: 'success',
                        duration: 3000
                    })
                    this.startVerificationCodeCooldown()
                }).catch(err => {
                    this.alertMessage(err.response.data.message, 'エラー')
                })
            },
            handleEditInformMember () {
                this.$refs['memberChangePassword'].validate((valid) => {
                    if (valid) {
                        this.disabled = true
                        this.isConfirm = true
                        let dataRequest = {
                            'loginId': this.currentUser.loginId,
                            'oldPassword': this.password.oldPassword,
                            'newPassword': this.password.newPassword,
                            'verificationCode': this.password.verificationCode
                        }
                        MemberClientApi.changePass(dataRequest).then(response => {
                            const apiToken = response.headers['api-token']
                            JwtService.saveToken(apiToken)
                            // リフレッシュトークンの取得と保存
                            const refreshToken = response.headers['api-refresh-token']
                            JwtService.saveRefreshToken(refreshToken)
                            ApiService.setHeader()
                            this.$msgbox({
                              title: 'パスワードの変更を完了しました。',
                              showClose: true,
                              center: true,
                              confirmButtonClass: 'btn-msg-box'
                            })
                            setTimeout(() => { this.$msgbox.close() }, 2000)
                            this.isComplete = true
                        }).catch(err => {
                            this.disabled = false
                            // 誤ったOTCは検証済み(消費不可)、正しいOTCは既に消費済み - どちらにせよ
                            // 古い値を残す意味がない
                            this.password.verificationCode = ''
                            this.alertMessage(err.response.data.message, '入力情報エラー')
                        })
                    } else {
                      this.alertMessage('入力情報を再度ご確認ください。', '入力情報エラー')
                    }
                })
            },
            alertMessage (message, title) {
              this.$alert(message, title, {
                  confirmButtonText: this.$t('commons.ok')
              })
            }
        }
    }
</script>

<style>
    @import 'member_register.css';
    @import '../../../../assets/css/guided_steps.css';
    .member-edit {
        padding-bottom: 2em;
    }
    @media screen and (min-width:769px) {
        .form th {
            width: 30%;
        }
    }
</style>
